import {
  Table,
  Thead,
  Tbody,
  Tr,
  TableContainer,
  Flex,
  Avatar,
  AvatarGroup,
  Spinner,
  Center,
  Badge,
  Text,
  Th,
} from "@chakra-ui/react";
import NoResults from "components/elements/NoResults";
import TableCell from "components/elements/TableCell";
import WidgetBox from "components/elements/WidgetBox/widget-box";
import PageHeader from "components/layout/PageHeader";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AVATAR_URL } from "utils/constants";
import AddBorrower from "./elements/AddBorrower";
import { useCustomFormats } from "hooks/useCustomFormat";
import { Pagination } from "components/elements/Pagination/pagination";
import InputSearch from "components/elements/InputSearch/input-search";
import { LinkMessage } from "components/elements/LinkMessage/link-message";
import { useBorrowers } from "hooks/useBorrowers";
import InviteButton from "../Settings/UserPermissions/InviteButton";

const BorrowerBadge = ({ borrower }: { borrower: any }) => {
  if (!borrower.invited) {
    return (
      <Badge color="#9E9AA3" bg="#C6C1D120" fontSize="14px" fontWeight="600">
        Not Invited
      </Badge>
    );
  }

  return <></>;
};

function Borrowers() {
  const { loading, borrowers, fetchBorrowers } = useBorrowers();
  const [filteredBorrowers, setFilteredBorrowers] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const { formatDate } = useCustomFormats();
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;

  const totalPages = Math.ceil(filteredBorrowers.length / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const visibleBorrowers = filteredBorrowers.slice(startIndex, startIndex + PAGE_SIZE);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  useEffect(() => {
    const filtered = borrowers.filter(borrower =>
      `${borrower.first_name} ${borrower.last_name}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    );
    setFilteredBorrowers(filtered || []);
    setCurrentPage(1);
  }, [searchQuery, borrowers]);

  return (
    <>
      <PageHeader title="Borrowers" />
      {loading ? (
        <Center>
          <Spinner />
        </Center>
      ) : (
        <>
          <WidgetBox>
            <Flex flexDirection={"column"} gap={4}>
              <Flex justifyContent={"space-between"} gap={4}>
                <InputSearch
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  width="300px"
                />
                <AddBorrower onLoad={fetchBorrowers} />
              </Flex>
              {visibleBorrowers.length > 0 ? (
                <TableContainer>
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Name</Th>
                        <Th>Company</Th>
                        <Th>Entity</Th>
                        <Th>Contacts</Th>
                        <Th>Status</Th>
                        <Th>Invited</Th>
                        <Th></Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {visibleBorrowers.map(borrower => (
                        <Tr key={borrower.id}>
                          <TableCell>
                            <Link
                              data-cy="borrower-name-table-cell"
                              to={`/borrowers/${borrower.id}`}
                              style={{
                                width: "100%",
                                display: "flex",
                              }}
                            >
                              <Text
                                as="span"
                                display="inline-block"
                                color="black"
                                cursor="pointer"
                                mr="auto"
                              >
                                {borrower.first_name} {borrower.last_name}{" "}
                              </Text>
                            </Link>
                          </TableCell>
                          <TableCell>
                            <Link to={`/companies/${borrower.company.id}`}>
                              {borrower.company.name}
                            </Link>
                          </TableCell>
                          <TableCell>
                            {borrower.entity ? (
                              <Link
                                to={`/companies/${borrower.company.id}/entities/${borrower.entity.id}`}
                              >
                                {borrower.entity.name}
                              </Link>
                            ) : (
                              "Not Provided"
                            )}
                          </TableCell>
                          <TableCell>
                            <AvatarGroup max={3} size="sm">
                              {borrower.contacts.map((contact: any) => (
                                <Avatar
                                  key={contact.id}
                                  src={`${AVATAR_URL}${contact.user.id}`}
                                  name={`${contact.user.first_name} ${contact.user.last_name}`}
                                />
                              ))}
                            </AvatarGroup>
                          </TableCell>
                          <TableCell>
                            <BorrowerBadge borrower={borrower} />
                          </TableCell>
                          <TableCell>{formatDate(borrower.created_at)}</TableCell>
                          <TableCell>
                            <Flex justifyContent="flex-end" alignItems="center" gap={2}>
                              <LinkMessage borrowerId={borrower.id} />
                              <InviteButton id={borrower.id} />
                            </Flex>
                          </TableCell>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              ) : (
                <NoResults message={"You haven't invited any borrowers yet."} />
              )}

              <Pagination
                totalPages={totalPages}
                currentPage={currentPage}
                handlePageChange={handlePageChange}
                totalCount={borrowers.length}
                pageSize={PAGE_SIZE}
              />
            </Flex>
          </WidgetBox>
        </>
      )}
    </>
  );
}

export default Borrowers;
